import Script from 'next/script';
import React from 'react';

import { GOOGLE_CONSENT_MODE_SNIPPET, GTAG_SHIM_SNIPPET } from './google-consent-mode';

type GoogleTagManagerProps = {
    // A GTM *container* ID (`GTM-XXXXXXX`), not a GA4 measurement ID (`G-XXXXXXX`).
    // A container is loaded through gtm.js and manages its own tags; see GoogleAnalytics
    // for the gtag.js side.
    containerId: string;
    // Emits Google Consent Mode v2 signals before the container loads. Only enable when the
    // component is rendered exclusively after the visitor granted analytics consent.
    consentMode?: boolean;
};

/**
 * Loads a distributor-managed Google Tag Manager container.
 *
 * The `<noscript>` iframe belongs immediately after `<body>` per Google's install
 * instructions, but it is rendered here — inside the consent gate — on purpose: our consent
 * banner is JavaScript-driven, so a visitor without JavaScript can never grant consent, and
 * a server-rendered iframe would fire for exactly those visitors without any legal basis.
 * Same trade-off the Meta pixel already makes.
 */
function GoogleTagManager({ containerId, consentMode }: GoogleTagManagerProps): React.JSX.Element | null {
    if (!containerId) {
        return null;
    }
    return (
        <>
            <Script
                id="gtm-init"
                strategy="afterInteractive"
                dangerouslySetInnerHTML={{
                    __html: `${consentMode ? GOOGLE_CONSENT_MODE_SNIPPET : GTAG_SHIM_SNIPPET}
            (function (w, d, s, l, i) {
                w[l] = w[l] || [];
                w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
                var f = d.getElementsByTagName(s)[0],
                    j = d.createElement(s),
                    dl = l != 'dataLayer' ? '&l=' + l : '';
                j.async = true;
                j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
                f.parentNode.insertBefore(j, f);
            })(window, document, 'script', 'dataLayer', '${containerId}');`
                }}
            />
            <noscript
                dangerouslySetInnerHTML={{
                    __html: `<iframe src="https://www.googletagmanager.com/ns.html?id=${containerId}"
height="0" width="0" style="display:none;visibility:hidden"></iframe>`
                }}
            />
        </>
    );
}

export default GoogleTagManager;
